import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { cacheGet, cachePut, enqueue, isNetworkError, saveOfflineFile } from "@/lib/offline";
import { useAuth } from "@/context/AuthContext";
import { useCanEdit } from "@/lib/permissions";
import { imageSrc } from "@/pages/Products";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  FileText,
  Plus,
  Trash2,
  Film,
  Images,
  Ship,
  Mail,
  Play,
  Clapperboard,
} from "lucide-react";

function embedUrl(url) {
  const yt = (url || "").match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{6,})/,
  );
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = (url || "").match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
}

const money = (v, cur) =>
  v != null && v !== ""
    ? new Intl.NumberFormat(undefined, { style: "currency", currency: cur || "USD", maximumFractionDigits: 0 }).format(v)
    : null;

const STYLE = `
@keyframes vdRise { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
.vd-rise { animation: vdRise 0.6s cubic-bezier(0.22,1,0.36,1) both; }
.vd-rise-1 { animation-delay: 0.05s; } .vd-rise-2 { animation-delay: 0.14s; }
.vd-rise-3 { animation-delay: 0.23s; } .vd-rise-4 { animation-delay: 0.32s; }
@media (prefers-reduced-motion: reduce) { .vd-rise { animation: none; } }
`;

export default function VesselDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = useCanEdit();
  const isStaff = user?.role === "admin" || user?.role === "staff";
  const edit = isStaff && canEdit;
  const canEnquire = user?.role === "client" || user?.role === "admin";
  const [v, setV] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingHeroVideo, setUploadingHeroVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [videoTitle, setVideoTitle] = useState("");
  const [enquiry, setEnquiry] = useState({ open: false, message: "", submitting: false });
  const fileRef = useRef(null);
  const videoFileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/vessels/${id}`);
      setV(data);
      cachePut(`vessel:${id}`, data);
    } catch (e) {
      if (isNetworkError(e)) {
        const cached = await cacheGet(`vessel:${id}`);
        if (cached) {
          setV(cached);
          toast.message("Offline — showing the last saved copy of this vessel");
          return;
        }
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Vessel not found");
    }
  };
  useEffect(() => {
    load();
    api.post("/activity/track", { type: "vessel_view", vessel_id: id }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const saveMedia = async (gallery, videos, heroVideoPath) => {
    const body = { gallery, videos };
    if (heroVideoPath !== undefined) body.hero_video_path = heroVideoPath;
    try {
      const { data } = await api.put(`/vessels/${id}/media`, body);
      setV(data);
      cachePut(`vessel:${id}`, data);
    } catch (e) {
      if (isNetworkError(e)) {
        await enqueue({ type: "vessel_media", targetId: id, payload: body });
        const next = { ...v, gallery, videos };
        if (heroVideoPath !== undefined) next.hero_video_path = heroVideoPath;
        setV(next);
        cachePut(`vessel:${id}`, next);
        toast.success("Saved offline — will sync when you're back online");
        return;
      }
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  const setCaption = (i, val) =>
    setV((cur) => ({
      ...cur,
      gallery: cur.gallery.map((x, xi) => (xi === i ? { ...x, caption: val } : x)),
    }));

  const onGalleryFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const added = [];
      const failed = [];
      // Upload in parallel batches of 3 so a big multi-select doesn't
      // hammer the proxy, while still being much faster than one-by-one.
      for (let i = 0; i < files.length; i += 3) {
        const batch = files.slice(i, i + 3);
        const results = await Promise.allSettled(
          batch.map(async (file) => {
            try {
              const fd = new FormData();
              fd.append("file", file);
              const { data } = await api.post("/upload", fd);
              return { path: data.path, caption: "" };
            } catch (err) {
              if (isNetworkError(err) && file.type.startsWith("image/")) {
                const placeholder = await saveOfflineFile(file);
                return { path: placeholder, caption: "" };
              }
              throw err;
            }
          })
        );
        results.forEach((r, ri) => {
          if (r.status === "fulfilled") added.push(r.value);
          else failed.push(batch[ri].name);
        });
      }
      if (added.length) {
        await saveMedia([...(v.gallery || []), ...added], v.videos || []);
        toast.success(added.length === 1 ? "Render added to gallery" : `${added.length} renders added to gallery`);
      }
      if (failed.length) {
        toast.error(`${failed.length} upload${failed.length > 1 ? "s" : ""} failed: ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}`);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onHeroVideoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingHeroVideo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      await saveMedia(v.gallery || [], v.videos || [], data.path);
      toast.success("Hero video set — it will autoplay when the page opens");
    } catch (err) {
      if (isNetworkError(err)) {
        toast.error("Video uploads need a connection — please try again when you're back online");
      } else {
        toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
      }
    } finally {
      setUploadingHeroVideo(false);
      if (videoFileRef.current) videoFileRef.current.value = "";
    }
  };

  const addVideo = () => {
    if (!embedUrl(videoUrl)) {
      toast.error("That doesn't look like a YouTube or Vimeo link");
      return;
    }
    saveMedia(v.gallery || [], [...(v.videos || []), { url: videoUrl.trim(), title: videoTitle.trim() }]);
    setVideoUrl("");
    setVideoTitle("");
    toast.success("Video added");
  };

  const submitEnquiry = async () => {
    setEnquiry((m) => ({ ...m, submitting: true }));
    try {
      await api.post("/quote-requests", { vessel_id: id, message: enquiry.message });
      toast.success("Thanks — our team will be in touch shortly");
      setEnquiry({ open: false, message: "", submitting: false });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not send enquiry");
      setEnquiry((m) => ({ ...m, submitting: false }));
    }
  };

  if (!v)
    return (
      <div className="p-8 text-[#5B6B7C] text-sm bg-white min-h-full" data-testid="vessel-detail-loading">
        Loading vessel…
      </div>
    );

  const hero = (v.gallery || [])[0]?.path || v.render_path;
  const price = money(v.price, v.price_currency);
  const pp = (v.principal_particulars || []).filter((s) => (s.value || "").trim());
  const gallery = v.gallery || [];

  return (
    <div className="bg-white min-h-full" data-testid="vessel-detail-page">
      <style>{STYLE}</style>
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20 pt-6" data-testid="vessel-layout-showcase">
        <div className="grid grid-cols-12 gap-8 lg:gap-12">
          {/* Left visual story */}
          <div className="col-span-12 lg:col-span-8 space-y-12">
            <div className="vd-rise">
              <HeroMedia v={v} hero={hero} />
            </div>
            {(v.tagline || v.description) && (
              <section className="vd-rise vd-rise-1" data-testid="vessel-detail-description">
                {v.tagline && <p className="text-[#0088CC] text-sm font-semibold mb-3">{v.tagline}</p>}
                <p className="text-[16px] leading-[1.8] text-[#334155] whitespace-pre-line">{v.description}</p>
              </section>
            )}
            <section className="vd-rise vd-rise-2" data-testid="vessel-detail-gallery">
              <h2 className="text-xs uppercase tracking-[0.25em] text-[#004AAD] font-bold mb-4 flex items-center gap-2">
                <Images className="h-3.5 w-3.5" /> Renders & Photos
              </h2>
              {gallery.length === 0 ? (
                <p className="text-sm text-[#5B6B7C]">No renders added yet.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {gallery.map((g, i) => (
                    <figure key={i} className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden" data-testid={`vessel-gallery-item-${i}`}>
                      <img src={imageSrc(g.path)} alt={g.caption || v.name} className="w-full aspect-[16/10] object-cover hover:scale-[1.02] transition-transform" />
                      <figcaption className="p-2 flex items-center gap-2">
                        {edit ? (
                          <>
                            <Input
                              value={g.caption || ""}
                              placeholder="Caption (optional)"
                              onChange={(e) => setCaption(i, e.target.value)}
                              onBlur={() => saveMedia(v.gallery, v.videos || [])}
                              className="h-7 bg-white border-[#E2E8F0] text-[#0F172A] text-xs"
                              data-testid={`vessel-gallery-caption-${i}`}
                            />
                            <button type="button" onClick={() => saveMedia(v.gallery.filter((_, xi) => xi !== i), v.videos || [])} className="text-[#5B6B7C] hover:text-red-500 shrink-0" data-testid={`vessel-gallery-remove-${i}`}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        ) : (
                          g.caption && <span className="text-xs text-[#64748B]">{g.caption}</span>
                        )}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </section>
            <section className="vd-rise vd-rise-3" data-testid="vessel-detail-videos">
              <h2 className="text-xs uppercase tracking-[0.25em] text-[#004AAD] font-bold mb-4 flex items-center gap-2">
                <Film className="h-3.5 w-3.5" /> Videos
              </h2>
              {(v.videos || []).length === 0 ? (
                <p className="text-sm text-[#5B6B7C]">No videos added yet.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {v.videos.map((vid, i) => (
                    <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] shadow-sm overflow-hidden" data-testid={`vessel-video-item-${i}`}>
                      <div className="aspect-video bg-black">
                        <iframe src={embedUrl(vid.url)} title={vid.title || `Video ${i + 1}`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      </div>
                      <div className="p-2 flex items-center justify-between gap-2">
                        <span className="text-xs text-[#334155] truncate">{vid.title || vid.url}</span>
                        {edit && (
                          <button type="button" onClick={() => saveMedia(v.gallery || [], v.videos.filter((_, xi) => xi !== i))} className="text-[#5B6B7C] hover:text-red-500 shrink-0" data-testid={`vessel-video-remove-${i}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
            {v.ga_preview_path && (
              <section className="vd-rise vd-rise-4" data-testid="vessel-detail-ga">
                <h2 className="text-xs uppercase tracking-[0.25em] text-[#004AAD] font-bold mb-4">General Arrangement</h2>
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-8">
                  <div className="relative" onContextMenu={(e) => e.preventDefault()}>
                    <img
                      src={imageSrc(v.ga_preview_path)}
                      alt={`${v.name} GA`}
                      className="w-full max-h-[560px] object-contain bg-white select-none pointer-events-none"
                      draggable={false}
                    />
                    <div className="absolute inset-0 z-10" aria-hidden="true" />
                  </div>
                </div>
              </section>
            )}
            {edit && (
              <div className="flex flex-wrap items-center gap-2" data-testid="vessel-media-controls">
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onGalleryFile} data-testid="vessel-gallery-file-input" />
                <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} className="border-[#CBD5E1] bg-white text-[#334155] hover:bg-[#F1F5F9] h-8 text-xs" data-testid="vessel-gallery-add">
                  <Plus className="h-3 w-3 mr-1" /> {uploading ? "Uploading…" : "Add renders / photos"}
                </Button>
                <input ref={videoFileRef} type="file" accept="video/mp4,video/webm,video/quicktime,video/x-m4v" className="hidden" onChange={onHeroVideoFile} data-testid="vessel-hero-video-input" />
                <Button size="sm" variant="outline" disabled={uploadingHeroVideo} onClick={() => videoFileRef.current?.click()} className="border-[#CBD5E1] bg-white text-[#334155] hover:bg-[#F1F5F9] h-8 text-xs" data-testid="vessel-hero-video-add">
                  <Clapperboard className="h-3 w-3 mr-1" /> {uploadingHeroVideo ? "Uploading…" : v.hero_video_path ? "Replace hero video" : "Set hero video"}
                </Button>
                {v.hero_video_path && (
                  <Button size="sm" variant="outline" onClick={() => saveMedia(v.gallery || [], v.videos || [], "")} className="border-[#FCA5A5] bg-white text-red-500 hover:bg-red-50 h-8 text-xs" data-testid="vessel-hero-video-remove">
                    <Trash2 className="h-3 w-3 mr-1" /> Remove hero video
                  </Button>
                )}
                <Input value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} placeholder="Paste a YouTube or Vimeo link" className="h-8 bg-white border-[#E2E8F0] text-[#0F172A] text-xs flex-1 min-w-[200px]" data-testid="vessel-video-url" />
                <Input value={videoTitle} onChange={(e) => setVideoTitle(e.target.value)} placeholder="Title (optional)" className="h-8 bg-white border-[#E2E8F0] text-[#0F172A] text-xs w-40" data-testid="vessel-video-title" />
                <Button size="sm" onClick={addVideo} className="bg-[#0088CC] hover:bg-[#006DA6] text-white h-8 text-xs" data-testid="vessel-video-add">
                  <Plus className="h-3 w-3 mr-1" /> Add video
                </Button>
              </div>
            )}
          </div>

          {/* Sticky floating details card */}
          <div className="col-span-12 lg:col-span-4">
            <div className="lg:sticky lg:top-6 vd-rise vd-rise-1">
              <div className="bg-white rounded-2xl border border-[#E2E8F0] shadow-[0_20px_50px_-12px_rgba(2,6,23,0.14)] p-7 space-y-7" data-testid="vessel-detail-sidebar">
                <div>
                  <Link to="/vessels" className="inline-flex items-center gap-1 text-[11px] text-[#64748B] hover:text-[#0F172A] mb-2" data-testid="vessel-detail-back">
                    <ArrowLeft className="h-3 w-3" /> All vessels
                  </Link>
                  <h1 className="font-display text-3xl xl:text-4xl font-bold text-[#0F172A] leading-tight">{v.name}</h1>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {v.sub_category && <span className="text-[11px] uppercase tracking-[0.2em] text-[#0088CC] font-bold">{v.sub_category}</span>}
                    {v.vessel_type && <span className="text-[11px] text-[#64748B]">{v.vessel_type}</span>}
                    {isStaff && (
                      <span className={`text-[10px] px-2 py-0.5 rounded ${v.is_live === false ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {v.is_live === false ? "Draft" : "Live"}
                      </span>
                    )}
                  </div>
                  {price && (
                    <div className="mt-3 flex items-baseline gap-2" data-testid="vessel-detail-price">
                      <span className="text-[11px] uppercase tracking-widest text-[#5B6B7C]">From</span>
                      <span className="font-display text-2xl font-bold text-[#004AAD] num">{price}</span>
                      <span className="text-sm font-semibold text-[#64748B]">{v.price_currency || "USD"}</span>
                    </div>
                  )}
                  <div className="mt-4 flex items-center gap-2 flex-wrap">
                    <Link to={`/vessels/${v.id}/flyer`} data-testid="vessel-detail-flyer-btn">
                      <Button size="sm" className="bg-[#0088CC] hover:bg-[#006DA6] text-white">
                        <FileText className="h-4 w-4 mr-1.5" /> View flyer
                      </Button>
                    </Link>
                    {canEnquire && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEnquiry((m) => ({ ...m, open: true }))}
                        className="border-[#0088CC] text-[#0088CC] bg-white hover:bg-[#0088CC]/10"
                        data-testid="vessel-detail-request-quote-btn"
                      >
                        <Mail className="h-4 w-4 mr-1.5" /> Request quote
                      </Button>
                    )}
                  </div>
                </div>
                {pp.length > 0 && (
                  <section data-testid="vessel-detail-pp">
                    <h2 className="text-xs uppercase tracking-[0.25em] text-[#004AAD] font-bold mb-2">Principal Particulars</h2>
                    <div>
                      {pp.map((s, i) => (
                        <div key={i} className="flex items-baseline justify-between gap-4 py-2 border-b border-[#F1F5F9]">
                          <span className="text-[10px] uppercase tracking-widest text-[#5B6B7C]">{s.label}</span>
                          <span className="text-sm font-medium text-[#0F172A] text-right">{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={enquiry.open} onOpenChange={(o) => (!o ? setEnquiry((m) => ({ ...m, open: false })) : null)}>
        <DialogContent className="bg-white border-[#E2E8F0] text-[#0F172A]" data-testid="enquiry-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Request a quote</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-[#64748B]">
              Send the Southern Ocean Solutions sales team a note about
              <span className="text-[#0F172A] font-semibold mx-1">{v.name}</span>
              — we&apos;ll reply to your registered email and phone.
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="rounded border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <div className="uppercase tracking-wider text-[10px] text-[#5B6B7C]">From</div>
                <div className="mt-1 text-[#0F172A]">{user?.name}</div>
                {user?.company && <div className="text-[#64748B]">{user.company}</div>}
              </div>
              <div className="rounded border border-[#E2E8F0] bg-[#F8FAFC] p-3">
                <div className="uppercase tracking-wider text-[10px] text-[#5B6B7C]">Contact</div>
                <div className="mt-1 text-[#0F172A]">{user?.email}</div>
                {user?.phone && <div className="text-[#64748B]">{user.phone}</div>}
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#64748B]">
                Message <span className="normal-case text-[#5B6B7C]">(optional)</span>
              </Label>
              <Textarea
                value={enquiry.message}
                onChange={(e) => setEnquiry((m) => ({ ...m, message: e.target.value }))}
                placeholder="Anything specific you'd like us to address — timeline, customisations, delivery location, budget…"
                className="mt-1.5 bg-white border-[#E2E8F0] text-[#0F172A] min-h-[140px]"
                data-testid="enquiry-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setEnquiry((m) => ({ ...m, open: false }))} disabled={enquiry.submitting} className="border-[#E2E8F0] text-[#64748B] hover:text-[#0F172A]" data-testid="enquiry-cancel-btn">
              Cancel
            </Button>
            <Button type="button" onClick={submitEnquiry} disabled={enquiry.submitting} className="bg-[#0088CC] hover:bg-[#006DA6] text-white" data-testid="enquiry-submit-btn">
              {enquiry.submitting ? "Sending…" : "Send request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Hero media — uploaded video autoplays (muted) on page open, hands back to
   the hero image when it ends, and can be replayed from the image. */
function HeroMedia({ v, hero }) {
  const [playing, setPlaying] = useState(!!v.hero_video_path);
  useEffect(() => setPlaying(!!v.hero_video_path), [v.hero_video_path, v.id]);

  if (v.hero_video_path && playing)
    return (
      <video
        src={imageSrc(v.hero_video_path)}
        className="w-full max-h-[70vh] object-contain bg-black rounded-xl"
        autoPlay
        muted
        playsInline
        controls
        onEnded={() => setPlaying(false)}
        data-testid="vessel-hero-video"
      />
    );

  return (
    <div className="relative">
      {hero ? (
        <img src={imageSrc(hero)} alt={v.name} className="w-full max-h-[70vh] object-contain" />
      ) : (
        <div className="h-64 flex items-center justify-center text-[#33475C] bg-[#F8FAFC] rounded-xl">
          <Ship className="h-20 w-20" />
        </div>
      )}
      {v.hero_video_path && (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="absolute inset-0 m-auto h-16 w-16 rounded-full bg-black/55 backdrop-blur flex items-center justify-center text-white hover:bg-black/70 transition-colors"
          title="Play video"
          data-testid="vessel-hero-video-play"
        >
          <Play className="h-7 w-7 ml-0.5" fill="currentColor" />
        </button>
      )}
    </div>
  );
}
